import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer, Box, Stack, Typography, IconButton, Divider, Chip,
  TextField, Button, CircularProgress, Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import HistoryIcon from "@mui/icons-material/History";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import api from "../services/api";

const toLicenca = (chave) => `/licencas/novo/${encodeURIComponent(chave)}`;

const fmtDate = (d) => {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return String(d || ""); }
};

function LogRow({ item }) {
  const move = item.acao === "move";
  const created = item.acao === "create";
  const archived = item.acao === "archive";
  const unarchived = item.acao === "unarchive";

  const label = move
    ? `Movido: ${item.de_coluna || "—"} → ${item.para_coluna || "—"}`
    : created
      ? `Card criado em "${item.para_coluna || item.de_coluna || "novo"}"`
      : archived
        ? "Card arquivado"
        : unarchived
          ? `Desarquivado para "${item.para_coluna || "novo"}"`
          : (item.acao || "log");

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
      <HistoryIcon fontSize="small" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">{label}</Typography>
        {item.observacao ? (
          <Typography variant="caption" color="text.secondary">{item.observacao}</Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {fmtDate(item.created_at)}
        </Typography>
      </Box>
    </Stack>
  );
}

function NoteRow({ item }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
      <ChatBubbleOutlineIcon fontSize="small" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {item.texto}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {fmtDate(item.created_at)}
          {item.autor?.chave ? ` • ${item.autor.chave}` : ""}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function CardTimelineDrawer({ open, onClose, card }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [noteText, setNoteText] = useState("");

  const headerBadge = useMemo(() => {
    if (card?.origem === "vencida") return "Cobrança";
    return null;
  }, [card]);

  useEffect(() => {
    const reset = () => { setItems([]); setCursor(null); setNoteText(""); };
    if (!open || !card?.id) { reset(); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/kanban/cards/${card.id}/timeline`, { params: { limit: 30 } });
        setItems(data?.items || []);
        setCursor(data?.next_cursor || null);
      } catch (e) {
        console.error('timeline load failed', e?.response?.status, e?.response?.data);
      } finally { setLoading(false); }
    })();
  }, [open, card?.id]);

  const loadMore = async () => {
    if (!cursor || !card?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/kanban/cards/${card.id}/timeline`, { params: { limit: 30, cursor } });
      setItems(prev => [...prev, ...(data?.items || [])]);
      setCursor(data?.next_cursor || null);
    } catch (e) {
      console.error('timeline more failed', e?.response?.status, e?.response?.data);
    } finally { setLoading(false); }
  };

  const addNote = async () => {
    const texto = noteText.trim();
    if (!texto) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/kanban/cards/${card.id}/notes`, { texto });
      if (data?.note) {
        setItems(prev => [data.note, ...prev]); // entra no topo
        setNoteText("");
      }
    } catch (e) {
      alert(e?.response?.data?.error || "Não foi possível salvar a anotação.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 460, maxWidth: "100vw" } }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap title={card?.cliente_nome_snapshot || ""}>
              {card?.cliente_nome_snapshot || "—"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "ui-monospace,Menlo,monospace" }}
                title={card?.chave_licenca}
              >
                {card?.chave_licenca}
              </Typography>
              <Tooltip title="Abrir licença">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); window.open(toLicenca(card?.chave_licenca), "_blank"); }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              {headerBadge && <Chip size="small" label={headerBadge} />}
            </Stack>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Campo de anotação rápida */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Adicionar anotação…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            multiline
            maxRows={4}
          />
          <Button
            variant="contained"
            startIcon={<NoteAddIcon />}
            onClick={addNote}
            disabled={adding || !noteText.trim()}
          >
            Salvar
          </Button>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Timeline */}
        <Box sx={{ minHeight: 160 }}>
          {items.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">Sem histórico ainda.</Typography>
          )}

          {items.map((it) =>
            it.type === "note"
              ? <NoteRow key={`n-${it.id}`} item={it} />
              : <LogRow  key={`l-${it.id}`} item={it} />
          )}

          {loading && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
            </Stack>
          )}

          {cursor && !loading && (
            <Stack alignItems="center" sx={{ pt: 1 }}>
              <Button size="small" startIcon={<ArrowDownwardIcon />} onClick={loadMore}>
                Carregar mais
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
